import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Cursor } from "@/components/Cursor";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/sections/Hero";
import { About } from "@/components/sections/About";
import { Projects } from "@/components/sections/Projects";
import { Experience } from "@/components/sections/Experience";
import { Testimonials } from "@/components/sections/Testimonials";
import { Blog } from "@/components/sections/Blog";
import { Contact } from "@/components/sections/Contact";
import { Footer } from "@/components/sections/Footer";
import BlogListPage from "@/pages/BlogListPage";
import BlogAboutPage from "@/pages/BlogAboutPage";
import BlogContactPage from "@/pages/BlogContactPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ImmbrainPage from "@/pages/ImmbrainPage";
import DownloadsPage from "@/pages/DownloadsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ListingsPage from "@/pages/ListingsPage";
import LandingPage from "@/pages/LandingPage";
import HubPage from "@/pages/HubPage";
import { AuthProvider } from "@/auth/AuthContext";
import SignInPage from "@/pages/SignInPage";

/*
 * ScrollHandler  -  sits inside BrowserRouter so it has router context.
 * Handles hash-based scroll after SPA navigation (e.g. /blog → /#about).
 * React Router does NOT trigger the browser's native hash scroll on
 * client-side transitions, so we do it manually here.
 * Only fires when a hash is present; individual pages (BlogListPage,
 * BlogPostPage) handle their own scroll-to-top via their own useEffect.
 */
function ScrollHandler() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace("#", "");

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return true;
      }
      return false;
    };

    // Try immediately; if the target section hasn't mounted yet
    // (Framer Motion / lazy render), retry after one frame.
    if (!scroll()) {
      const t = setTimeout(scroll, 120);
      return () => clearTimeout(t);
    }
  }, [pathname, hash]);

  return null;
}

const Home = () => {
  useEffect(() => {
    document.documentElement.classList.add("dark", "portfolio-mode");
    return () => {
      document.documentElement.classList.remove("portfolio-mode");
    };
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <Cursor />
      <Nav />
      <main>
        <Hero />
        <About />
        <Projects />
        <Experience />
        <Testimonials />
        <Blog />
        <Contact />
      </main>
      <Footer />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast: "bg-graphite-900 border border-edge-700 text-zinc-100",
            description: "text-zinc-400",
            actionButton: "bg-magenta-500 text-ink-950"
          }
        }}
      />
    </div>
  );
};

function ServicesOfflinePage() {
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.title = "Services temporarily offline | Malicke Case";
  }, []);

  return (
    <main className="min-h-[100dvh] bg-sand px-6 py-24 text-ink-mc">
      <div className="mx-auto max-w-xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">Temporary notice</p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-6xl">Services are offline for now.</h1>
        <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-soft-mc">
          The services page is unavailable while it is being updated. You can return to the preview hub or browse the sample listings.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/" className="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white">Back to preview hub</Link>
          <Link to="/listings" className="rounded-full border border-brand/20 px-5 py-3 text-sm font-semibold text-brand">View sample listings</Link>
        </div>
      </div>
    </main>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollHandler />
        <Routes>
          <Route path="/" element={<HubPage />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/portfolio" element={<Home />} />
          <Route path="/services" element={<ServicesOfflinePage />} />
          <Route path="/listings" element={<ListingsPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/about" element={<BlogAboutPage />} />
          <Route path="/blog/contact" element={<BlogContactPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/immbrain" element={<ImmbrainPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/account" element={<SignInPage account />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
